import React, { FC, useState } from 'react';
import { FR_PR_BLUE, FR_SP1, FR_SP3, FR_PR_PINK, FR_PR_YELLOW } from '@shared/styles/vars';
import { Avatar } from '@src/components/Avatar';
import { getNameInitials } from '@src/util/getNameInitials';
import api from '@src/util/api';
import { ERROR_CODES } from '@src/constants';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { useAppDispatch } from '@src/redux';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { useCancelTeamInviteMutation } from '@src/graphql/generated';
import { InviteState } from './enums/inviteState.enum';
import { InviteProps } from './props';
import './styles.scss';

const COLOR_PALETTE = [FR_PR_BLUE, FR_SP1, FR_SP3, FR_PR_PINK, FR_PR_YELLOW];

export const Invite: FC<InviteProps> = ({ index, email, fullName, inviteId, state }) => {
  const dispatch = useAppDispatch();

  const [isResendingInvite, setIsResendingInvite] = useState(false);

  const [cancelTeamInvite, { loading: isCancellingInvite, data: cancelTeamInviteResult }] =
    useCancelTeamInviteMutation();

  if (cancelTeamInviteResult?.cancelTeamInvite === true) {
    return null;
  }

  const avatarColor = COLOR_PALETTE[index % 5];
  const nameInitials = getNameInitials(fullName || email);

  return (
    <div className="settings-team-invite-row" key={inviteId}>
      <div className="settings-team-invite-row__col settings-team-invite-row__col--name">
        <Avatar
          avatarColor={avatarColor}
          className="settings-team-invite-row__avatar"
          initials={nameInitials}
          size="large"
        />

        <span className="settings-team-invite-row__name">{fullName}</span>
      </div>

      <div className="settings-team-invite-row__col settings-team-invite-row__col--email">{email}</div>

      <div className="settings-team-invite-row__col settings-team-invite-row__col--state">
        {state === InviteState.ACCEPTED ? 'Invite accepted' : 'Invite sent'}
      </div>

      <div className="settings-team-invite-row__col settings-team-invite-row__col--resend">
        {state === InviteState.PENDING ? (
          isResendingInvite ? (
            <span className="settings-team-invite-row__sending">Sending...</span>
          ) : (
            <span
              className="settings-team-invite-row__resend-button"
              onClick={async () => {
                try {
                  setIsResendingInvite(true);
                  await api.put(`/api/team/invites/${inviteId}`);
                  dispatch(setSuccessFlashMessage('Invite successfully sent'));
                  setIsResendingInvite(false);
                } catch (error) {
                  dispatch(setErrorFlashMessage(ERROR_CODES[error.body && error.body.key]));
                  setIsResendingInvite(false);
                }

                trackEvent({
                  context: MetricContext.TEAM_MEMBERS,
                  interaction: MetricAction.RESEND_INVITE_TO_TEAM_MEMBER,
                });
              }}
            >
              Resend invite
            </span>
          )
        ) : null}
      </div>
      <div className="settings-team-invite-row__col settings-team-invite-row__col--resend">
        {state === InviteState.PENDING ? (
          isCancellingInvite ? (
            <span className="settings-team-invite-row__sending">Cancelling...</span>
          ) : (
            <span
              className="settings-team-invite-row__cancel-button"
              onClick={async () => {
                try {
                  await cancelTeamInvite({
                    variables: {
                      teamInviteId: inviteId,
                    },
                  });
                  dispatch(setSuccessFlashMessage('Invite successfully cancelled'));
                } catch (error) {
                  dispatch(setErrorFlashMessage());
                }

                trackEvent({
                  context: MetricContext.TEAM_MEMBERS,
                  interaction: MetricAction.RESEND_INVITE_TO_TEAM_MEMBER,
                });
              }}
            >
              Cancel invite
            </span>
          )
        ) : null}
      </div>
    </div>
  );
};
